/**
 * LED / Big-screen overlay — the main venue projector output.
 *
 * Design highlights:
 *   - Full-bleed dark-red background with diagonal streaks & tournament banner.
 *   - Left column: 3:4 player photo card with orange border.
 *   - Right column: name / batting / bowling / location / mobile "pills" +
 *     giant yellow bid number.
 *   - Custom SOLD / UNSOLD announcement — respects `sold_media_url` /
 *     `unsold_media_url` (PNG · GIF · MP4 · 3GP) uploaded by the organizer.
 *     Falls back to the giant rubber-stamp when no custom media is set.
 *   - Fan-view QR (bottom-left) points to the read-only Stream overlay so
 *     audiences at the venue can watch live from their own phones.
 */
import React from "react";
import { useParams } from "react-router-dom";
import { Trophy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { resolveImageUrl } from "@/components/ImageInput";
import {
  PHOTO_FALLBACK, useSaleAnnouncement, usePublicAuction, useOverlayState,
  FIELD_LABELS, readPlayerField, getLedDisplayFields, useAccentStyle, brandLogoFor,
} from "./_shared";

export function OverlayLED() {
  const { auctionId } = useParams();
  const { auction, teams, players, reload } = usePublicAuction(auctionId);
  const state = useOverlayState(auctionId, reload);

  const announce = useSaleAnnouncement(auctionId, players, teams);
  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const bannerUrl = auction?.banner_url ? resolveImageUrl(auction.banner_url) : null;
  const currentBid = state?.current_bid || 0;
  const displayTeam = announce?.kind === "sold" ? announce.team : bidder;
  // Iter23: custom per-tournament branding + accent colour
  const accent = auction?.accent_color || "#F97316";
  const accentStyle = useAccentStyle(auction);
  const brandLogo = brandLogoFor(auction);
  // Registration fields the superadmin marked for LED display (age/mobile/total_runs/etc.)
  const ledExtraFields = getLedDisplayFields(auction);

  return (
    <div className="min-h-screen text-white overflow-hidden relative led-bg" data-testid="overlay-led" style={{ fontFamily: "Inter, sans-serif", ...accentStyle }}>
      {bannerUrl && (
        <div className="absolute inset-0 z-0 opacity-20">
          <img src={bannerUrl} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      )}
      <div className="absolute inset-y-0 left-0 w-24 z-0" style={{ background: `linear-gradient(135deg, var(--accent-mid) 0%, transparent 100%)`, clipPath: "polygon(0 0, 100% 0, 60% 100%, 0 100%)" }} />
      <div className="absolute inset-y-0 right-0 w-40 z-0" style={{ background: `linear-gradient(225deg, var(--accent-mid) 0%, transparent 100%)`, clipPath: "polygon(40% 0, 100% 0, 100% 100%, 0 100%)" }} />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex items-start justify-between px-6 pt-4 relative">
          <div className="flex items-center gap-3 z-10">
            <img src={brandLogo} alt="Brand" className="h-10 sm:h-14 drop-shadow-2xl" data-testid="led-brand-logo" />
          </div>
          <div className="text-center flex-1">
            <div className="font-black uppercase text-yellow-400 text-3xl sm:text-5xl leading-tight tracking-wide drop-shadow-lg whitespace-nowrap overflow-hidden text-ellipsis" data-testid="led-tournament-name">
              {auction?.name || "AUCTION"}
            </div>
            <div className="flex justify-center items-stretch mt-1">
              <span className="text-black font-black uppercase text-2xl sm:text-4xl px-4 py-1 tracking-wider" style={{ background: "var(--accent-color)" }}>PLAYER</span>
              <span className="bg-white text-black font-black uppercase text-2xl sm:text-4xl px-4 py-1 tracking-wider">AUCTION</span>
            </div>
          </div>
          <div className="w-24" />
        </div>

        {!cur ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-4 relative">
            {bannerUrl ? (
              <div className="w-full max-w-5xl mx-auto rounded-2xl overflow-hidden border-4 border-yellow-400/60 shadow-2xl mb-6 bg-black/40" data-testid="led-idle-banner">
                <img src={bannerUrl} alt="" className="w-full h-auto max-h-[55vh] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            ) : (
              <img src={brandLogo} alt="" className="h-32 opacity-80 mb-6 drop-shadow-2xl" />
            )}
            <div className="font-black uppercase text-5xl text-yellow-400 drop-shadow-2xl" data-testid="led-idle-title">Next Player</div>
            {auction?.description && <div className="text-xl text-zinc-300 mt-3 max-w-2xl">{auction.description}</div>}
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-12 items-center gap-4 px-4 sm:px-8 py-6 relative">
            <div className="col-span-5 flex flex-col items-center gap-3 relative">
              {cur.category && (
                <div
                  className="absolute -top-2 -left-2 z-20 w-14 h-14 rounded-full grid place-items-center font-black text-white text-2xl shadow-2xl"
                  style={{ background: `linear-gradient(135deg, var(--accent-color), var(--accent-mid))` }}
                  data-testid="led-category-badge"
                >
                  {cur.category === "Icon" ? "★" : (cur.player_number || cur.category)}
                </div>
              )}
              <div
                className="relative rounded-xl overflow-hidden border-4 sm:border-[6px] shadow-2xl bg-black/40 flex items-center justify-center"
                style={{ borderColor: accent, width: "min(38vw, 460px)", aspectRatio: "3 / 4" }}
              >
                <img
                  src={cur.photo_url ? resolveImageUrl(cur.photo_url) : PHOTO_FALLBACK}
                  onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
                  alt=""
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-3 sm:p-4">
                  <div
                    className="font-black uppercase text-white text-2xl sm:text-4xl leading-tight"
                    style={{ textShadow: "0 3px 10px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9)" }}
                    data-testid="led-player-name"
                  >
                    {cur.name}
                  </div>
                  <div className="text-yellow-300 font-semibold text-sm sm:text-lg uppercase tracking-wider">
                    {cur.role} {cur.age ? `· ${cur.age} yrs` : ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-7 flex flex-col gap-3 sm:gap-4 relative">
              {cur.name && (
                <div className="led-pill text-base sm:text-2xl" data-testid="led-player-name-pill">
                  <span className="text-orange-300 mr-2">NAME :</span>{cur.name}
                </div>
              )}
              {cur.batting_style && (
                <div className="led-pill text-base sm:text-2xl" data-testid="led-player-batting">
                  <span className="text-orange-300 mr-2">BAT :</span>{cur.batting_style}
                </div>
              )}
              {cur.bowling_style && (
                <div className="led-pill text-base sm:text-2xl" data-testid="led-player-bowling">
                  <span className="text-orange-300 mr-2">BOWL :</span>{cur.bowling_style}
                </div>
              )}
              {cur.location && (
                <div className="led-pill text-base sm:text-2xl" data-testid="led-player-location">
                  <span className="text-orange-300 mr-2">FROM :</span>{cur.location}
                </div>
              )}
              {cur.mobile && (
                <div className="led-pill text-base sm:text-2xl" data-testid="led-player-mobile">
                  <span className="text-orange-300 mr-2">MOB :</span>{cur.mobile}
                </div>
              )}
              {/* Iter23: additional player fields the superadmin chose to display on LED */}
              {ledExtraFields.slice(0, 5).map((k) => {
                const v = readPlayerField(cur, k);
                if (v == null || v === "") return null;
                return (
                  <div key={k} className="led-pill text-base sm:text-xl" data-testid={`led-extra-${k}`}>
                    <span className="text-orange-300 mr-2">{FIELD_LABELS[k] || k.toUpperCase()} :</span>{v}
                  </div>
                );
              })}

              <div className="flex items-center justify-between gap-6 mt-2">
                <div className="flex-1">
                  <div className="text-white/70 text-xs sm:text-sm uppercase tracking-widest font-bold mb-1">
                    {announce?.kind === "sold" ? "SOLD FOR" : "CURRENT BID"}
                  </div>
                  <div
                    className="font-black text-yellow-400 leading-none drop-shadow-2xl"
                    style={{ fontSize: "clamp(64px, 10vw, 160px)", fontFamily: "Inter, sans-serif" }}
                    data-testid="led-current-bid"
                  >
                    {(announce?.kind === "sold" ? (announce.amount || currentBid) : currentBid).toLocaleString()}
                  </div>
                  <div className="text-white/60 text-xs uppercase tracking-widest">Points</div>
                </div>
                {displayTeam && (
                  <div className="flex flex-col items-center flex-shrink-0" data-testid="led-team-display">
                    {displayTeam.logo_url ? (
                      <img
                        src={resolveImageUrl(displayTeam.logo_url)}
                        alt=""
                        className="w-20 h-20 sm:w-32 sm:h-32 rounded-lg object-contain bg-black/30 border-2 shadow-2xl p-1"
                        style={{ borderColor: displayTeam.color || "#F97316" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-lg grid place-items-center bg-black/30 border-2 shadow-2xl" style={{ borderColor: displayTeam.color || "#F97316" }}>
                        <Trophy weight="fill" size={64} style={{ color: displayTeam.color || "#F97316" }} />
                      </div>
                    )}
                    <div className="font-black uppercase text-white text-lg sm:text-2xl mt-2 tracking-wider drop-shadow-lg text-center max-w-[240px]">{displayTeam.name}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {auction?.sponsor_text && (
          <div className="fixed bottom-14 right-4 z-20 bg-black/70 backdrop-blur border border-amber-500/40 rounded px-3 py-2" data-testid="led-sponsor-slot">
            <div className="label-mono text-amber-300 text-[10px]" style={{ fontWeight: 600 }}>POWERED BY</div>
            <div className="text-lg text-white leading-none" style={{ fontWeight: 600 }}>{auction.sponsor_text}</div>
          </div>
        )}

        {/* Fan-view QR — scans open the read-only Stream overlay on any phone */}
        <div
          className="fixed bottom-4 left-4 z-20 bg-white rounded-lg p-2 shadow-2xl border-4 border-amber-400/70 flex flex-col items-center gap-1"
          data-testid="led-fan-qr"
        >
          <QRCodeSVG
            value={`${typeof window !== "undefined" ? window.location.origin : ""}/overlay/${auctionId}/stream`}
            size={92}
            level="M"
            includeMargin={false}
          />
          <div className="text-black text-[10px] font-black uppercase tracking-widest leading-tight text-center" style={{ letterSpacing: "1px" }}>
            Scan &<br />Watch Live
          </div>
        </div>
      </div>

      {announce && (() => {
        const isSoldAnnounce = announce.kind === "sold";
        const customUrl = isSoldAnnounce ? auction?.settings?.sold_media_url : auction?.settings?.unsold_media_url;
        if (customUrl) {
          const isVid = [".3gp", ".3gpp", ".mp4", ".webm", ".mov"].some((e) => customUrl.split("?")[0].toLowerCase().endsWith(e));
          return (
            <div
              key={announce.key}
              className="fixed inset-0 flex items-center justify-center z-40 pointer-events-none"
              data-testid={`led-stamp-${announce.kind}`}
            >
              <div className="flex flex-col items-center animate-in fade-in slide-in-from-bottom duration-500">
                {isVid ? (
                  <video src={resolveImageUrl(customUrl)} className="max-h-[60vh] max-w-[85vw] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]" autoPlay muted loop playsInline />
                ) : (
                  <img src={resolveImageUrl(customUrl)} alt="" className="max-h-[60vh] max-w-[85vw] object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.6)]" onError={(e) => { e.target.style.display = "none"; }} />
                )}
                {announce.player && (
                  <div
                    className="text-3xl font-display uppercase text-white mt-3"
                    style={{ textShadow: "0 3px 10px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.9)" }}
                  >{announce.player.name}{isSoldAnnounce && announce.amount != null ? ` — ${announce.amount} pts` : ""}</div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div
            className="fixed inset-0 flex items-center justify-center z-40 pointer-events-none"
            data-testid={`led-stamp-${announce.kind}`}
          >
            <div
              className={`stamp-anim stamp-outline uppercase px-10 sm:px-16 py-3 sm:py-6 bg-white/10 backdrop-blur-sm ${announce.kind === "sold" ? "text-white" : "text-red-400"}`}
              style={{ fontSize: "clamp(72px, 12vw, 200px)" }}
            >
              {announce.kind === "sold" ? "SOLD" : "UNSOLD"}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
